import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { communityTrailsAPI, subscriptionAPI } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, MapPin, Star, Loader2, Mountain, Users, Crown,
  ChevronDown, ChevronUp, MessageSquare, Shield,
} from "lucide-react";

const DIFFICULTIES = ["Easy", "Moderate", "Difficult", "Expert", "Extreme"];

const difficultyColor = (d) => ({
  Easy: "bg-green-500/20 text-green-600 border-green-500/30",
  Moderate: "bg-blue-500/20 text-blue-600 border-blue-500/30",
  Difficult: "bg-amber-500/20 text-amber-600 border-amber-500/30",
  Expert: "bg-orange-500/20 text-orange-600 border-orange-500/30",
  Extreme: "bg-red-500/20 text-red-600 border-red-500/30",
}[d] || "bg-zinc-500/20 text-zinc-400");

function StarRating({ value, onChange, readonly = false }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          disabled={readonly}
          onClick={() => onChange?.(i)}
          onMouseEnter={() => !readonly && setHover(i)}
          onMouseLeave={() => !readonly && setHover(0)}
          className={`p-0.5 ${readonly ? "cursor-default" : "cursor-pointer"}`}
          data-testid={`star-${i}`}
        >
          <Star
            className={`w-5 h-5 transition-colors ${
              (hover || value) >= i
                ? "fill-amber-400 text-amber-400"
                : "text-zinc-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function CommunityTrails() {
  const navigate = useNavigate();
  const [trails, setTrails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [expandedTrail, setExpandedTrail] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    trail_name: "",
    location: "",
    rating: 3,
    difficulty: "Moderate",
    tip: "",
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [trailsRes, subRes] = await Promise.all([
        communityTrailsAPI.list(),
        subscriptionAPI.status(),
      ]);
      setTrails(trailsRes.data.trails || []);
      setIsPro(subRes.data.is_pro);
    } catch {
      toast.error("Failed to load community trails");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.trail_name.trim()) {
      toast.error("Trail name is required");
      return;
    }
    setSubmitting(true);
    try {
      await communityTrailsAPI.submit(form);
      toast.success("Trail rating submitted!");
      setShowSubmit(false);
      setForm({ trail_name: "", location: "", rating: 3, difficulty: "Moderate", tip: "" });
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Community Trails
              </h1>
              <p className="text-xs text-muted-foreground">
                {trails.length} trails rated by the community
              </p>
            </div>
          </div>
          {isPro ? (
            <Button onClick={() => setShowSubmit(true)} data-testid="submit-rating-btn">
              <Plus className="w-4 h-4 mr-2" /> Rate a Trail
            </Button>
          ) : (
            <Button variant="outline" onClick={() => navigate("/subscription")} data-testid="upgrade-btn">
              <Crown className="w-4 h-4 mr-2" /> Pro to Rate
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {trails.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Mountain className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-medium">No trails rated yet</p>
            <p className="text-sm">Be the first to share your trail experience!</p>
          </div>
        ) : (
          trails.map((trail) => {
            const isExpanded = expandedTrail === trail.trail_name;
            return (
              <Card key={trail.trail_name} data-testid={`trail-card-${trail.trail_name}`}>
                <CardContent className="pt-4">
                  <div
                    className="flex items-start justify-between cursor-pointer"
                    onClick={() => setExpandedTrail(isExpanded ? null : trail.trail_name)}
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-base truncate">{trail.trail_name}</h3>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {trail.location && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="w-3 h-3" /> {trail.location}
                          </span>
                        )}
                        <Badge className={`text-xs ${difficultyColor(trail.difficulty)}`}>
                          {trail.difficulty}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                          <span className="font-bold text-sm">{trail.avg_rating}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {trail.total_ratings} {trail.total_ratings === 1 ? "rating" : "ratings"}
                        </span>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>

                  {isExpanded && trail.recent_tips?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3">
                      <h4 className="text-sm font-medium flex items-center gap-1.5">
                        <MessageSquare className="w-3.5 h-3.5" /> Tips & Reviews
                      </h4>
                      {trail.recent_tips.map((tip, i) => (
                        <div key={i} className="p-3 rounded-md bg-muted/50 text-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-xs">{tip.user_name || "Anonymous"}</span>
                            <div className="flex items-center gap-2">
                              {tip.difficulty && (
                                <Badge variant="outline" className="text-xs py-0">
                                  {tip.difficulty}
                                </Badge>
                              )}
                              {tip.rating && (
                                <span className="flex items-center gap-0.5 text-xs">
                                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                                  {tip.rating}
                                </span>
                              )}
                            </div>
                          </div>
                          {tip.tip && <p className="text-muted-foreground">{tip.tip}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </main>

      {/* Submit Rating Dialog */}
      <Dialog open={showSubmit} onOpenChange={setShowSubmit}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rate a Trail</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Trail Name *</Label>
              <Input
                value={form.trail_name}
                onChange={(e) => setForm({ ...form, trail_name: e.target.value })}
                placeholder="e.g., Rubicon Trail"
                data-testid="trail-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g., Placer County, CA"
                data-testid="trail-location-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Rating</Label>
              <StarRating value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
            </div>
            <div className="space-y-2">
              <Label>Difficulty</Label>
              <Select
                value={form.difficulty}
                onValueChange={(v) => setForm({ ...form, difficulty: v })}
              >
                <SelectTrigger data-testid="difficulty-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIFFICULTIES.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tips / Notes</Label>
              <Textarea
                value={form.tip}
                onChange={(e) => setForm({ ...form, tip: e.target.value })}
                placeholder="Share your experience or tips for other wheelers..."
                rows={3}
                data-testid="trail-tip-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmit(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-trail-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Rating
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
